// lib/koi-pond-v3/assets/ghostKoi.ts
//
// T12.1 — the ghost koi. A rare luminous variant that materializes out of the
// dark water, glides slowly across the visible band over ~16s, and dissolves
// again. It does NOT interact with the cursor or the other koi — it just
// drifts through. The "wait… was that real?" beat.
//
// Implementation notes:
//   - Reuses the shared koi geometry + the standard koi material, so it
//     undulates exactly like the resident koi (same SWIM vertex snippet).
//   - Paper-white body (`ghostKoi`) + cool-blue fresnel rim (`ghostRim`).
//   - The materialize/dissolve is driven through the material's uMoonDim
//     (which linearly scales the lit colour): eased 0→1→0 over the
//     traversal. Against the near-black water, fading the brightness to 0
//     reads as the fish dissolving into the dark — no transparency needed.
//   - Spawn cadence 90–180s (compressed under the review fast-cadence flag).

import { Mesh, Vector3, Quaternion, BufferGeometry } from 'three';
import { Color } from 'three';
import { makeKoiMaterialV3 } from '../shaders/koiMaterialV3';

const DURATION = 16; // seconds to cross the frame
const SPAWN_MIN = 90;
const SPAWN_MAX = 180;
const FAST_MIN = 8;
const FAST_MAX = 16;

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

export interface GhostKoiOptions {
  geometry: BufferGeometry;
  moonDir: Vector3;
  fogUniforms: { uFogColor: { value: Color }; uFogDensity: { value: number } };
  fastCadence?: boolean;
  sss?: boolean; // T12.5 — false disables subsurface scattering (mobile)
}

export function makeGhostKoi(opts: GhostKoiOptions) {
  const { material, uniforms } = makeKoiMaterialV3({
    baseKey: 'ghostKoi',
    patternStrength: 0,
    rimKey: 'ghostRim',
    groundCool: 1.0,
    swim: { phase: 0.4, speed: 1.9 }, // calmer beat than the resident koi
    sss: opts.sss,
    moonDir: opts.moonDir,
    fogUniforms: opts.fogUniforms,
  });
  // A touch brighter than the resident koi so it reads as self-lit/luminous
  // at the peak of its pass, not just another pale fish.
  uniforms.uMoonColor.value = new Color(0xffffff);

  const mesh = new Mesh(opts.geometry, material);
  mesh.scale.setScalar(1.5);
  mesh.visible = false;
  mesh.frustumCulled = false; // swim displacement can push it past the AABB

  const minGap = opts.fastCadence ? FAST_MIN : SPAWN_MIN;
  const maxGap = opts.fastCadence ? FAST_MAX : SPAWN_MAX;
  const nextGap = () => minGap + Math.random() * (maxGap - minGap);

  let active = false;
  let spawnT = -1;
  let nextSpawnT = -1; // set on first update once we know the clock
  const start = new Vector3();
  const end = new Vector3();
  const _pos = new Vector3();
  const _q = new Quaternion();
  const _fwd = new Vector3();
  const UP = new Vector3(0, 1, 0);

  function spawn(t: number) {
    active = true;
    spawnT = t;
    // Enter from a random side at mid-depth, cross to the far side. y in the
    // koi band (~-0.7..-0.2), z biased toward the lit midground.
    const leftToRight = Math.random() < 0.5;
    const y = -0.7 + Math.random() * 0.5;
    const z = -0.5 + Math.random() * 2.0;
    const x0 = leftToRight ? -5.5 : 5.5;
    start.set(x0, y, z);
    end.set(-x0, y + (Math.random() - 0.5) * 0.6, z + (Math.random() - 0.5) * 1.5);
  }

  function update(t: number) {
    if (nextSpawnT < 0) nextSpawnT = t + nextGap();

    if (!active) {
      if (t >= nextSpawnT) spawn(t);
      else return;
    }

    const p = (t - spawnT) / DURATION;
    if (p >= 1) {
      active = false;
      mesh.visible = false;
      nextSpawnT = t + nextGap();
      return;
    }

    // Position: ease across with a gentle vertical bob.
    const ease = p * p * (3 - 2 * p); // smoothstep — slow in/out at the edges
    _pos.lerpVectors(start, end, ease);
    _pos.y += Math.sin(p * Math.PI * 2) * 0.12;
    mesh.position.copy(_pos);

    // Orient along travel (mesh head is +X).
    _fwd.copy(end).sub(start).setY(0).normalize();
    const yaw = Math.atan2(_fwd.x, _fwd.z); // +X head → see koiPathFollowing
    _q.setFromAxisAngle(UP, yaw - Math.PI / 2);
    mesh.quaternion.copy(_q);

    // Materialize in over the first 22%, hold, dissolve over the last 28%.
    const fadeIn = clamp01(p / 0.22);
    const fadeOut = clamp01((1 - p) / 0.28);
    uniforms.uMoonDim.value = Math.min(fadeIn, fadeOut);

    uniforms.uTime.value = t;
    uniforms.uSwimTime.value = t;
    uniforms.uSwimGain.value = 0.8;

    mesh.visible = true;
  }

  function dispose() {
    material.dispose();
  }

  return { object: mesh, update, dispose };
}
